<template>
  <div class="helpForm">
    <div class="helpForm_container">
      <div class="helpForm_content">
        <FormulateForm @submit="handleSubmit" novalidate>
          <div :class="{ form_field: true, form_field_focused: active }">
            <div class="form_field_prefix">
              <IconSearch class="form_field_prefix_visible" />
              <IconSearch class="form_field_prefix_hidden" />
            </div>

            <FormulateInput
              type="text"
              name="search"
              v-model="formValues.search"
              placeholder="How can we help you?"
              @focusin="() => (active = true)"
              @focusout="() => (active = false)"
              @keyup="inputChange"
            >
              <template #help>
                <ul class="search_result" v-if="openVariants">
                  <li
                    v-for="item in searchVariant"
                    @click="() => setSearchValue(item)"
                    :key="item"
                  >
                    {{ item }}
                  </li>
                </ul>
              </template>
            </FormulateInput>
          </div>
        </FormulateForm>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import IconSearch from "@/components/icons/IconSearch.vue";
import { ref } from "vue";

interface FormValues {
  search: string | null;
}

const openVariants = ref();
const searchVariant = ref([] as Array<string>);
const active = ref();
const formValues = ref({
  search: null,
} as FormValues);

function handleSubmit(val: FormValues) {
  console.log(val);
}
function setSearchValue(val: string) {
  formValues.value.search = val;
  openVariants.value = false;
  handleSubmit({ search: val });
}

function inputChange() {
  if (formValues.value.search) {
    searchVariant.value = ["123", "345", "567"];
    openVariants.value = true;
  } else {
    searchVariant.value = [];
    openVariants.value = false;
  }
}
</script>

<style lang="scss">
.helpForm {
  width: calc(100% - 48px);
  max-width: 1806px;
  margin: 0 auto;
  padding: 120px 0 80px;
  position: relative;
  z-index: 2;
  @media screen and (max-width: 1540px) {
    width: calc(100% - 64px);
    padding: 68px 0 60px;
  }

  @media screen and (max-width: 1100px) {
    width: 100%;
    padding: 44px 0 45px;

    background-image: url("@assets/images/search.jpg");
    background-position: center center;
    background-size: 120%;
  }
  @media screen and (max-width: 767px) {
    padding: 32px 0;
  }

  &_content {
    max-width: 1200px;
    margin: 0 auto;

    @media screen and (max-width: 1540px) {
      max-width: 1080px;
    }
    @media screen and (max-width: 1380px) {
      max-width: 920px;
    }

    @media screen and (max-width: 1100px) {
      max-width: 100%;
      padding: 0 94px;
    }
    @media screen and (max-width: 767px) {
      padding: 0 16px;
    }
  }

  .form_field {
    @media screen and (max-width: 1100px) {
      margin-bottom: 0;
    }
    input {
      font-family: "ITC Avant Garde Gothic Std";
      background-color: transparent;
      border: none;
      font-size: 34px;
      font-style: normal;
      font-weight: 600;
      line-height: 175%;
      color: #ffffff;
      padding: 18px 40px 18px 96px;
      caret-color: #40acff;

      @media screen and (max-width: 1540px) {
        font-size: 22px;
        padding: 18px 40px 18px 88px;
      }

      @media screen and (max-width: 1100px) {
        font-size: 20px;
        padding: 14px 40px 14px 60px;
      }

      @media screen and (max-width: 767px) {
        font-size: 18px;
        padding: 12px 40px 12px 54px;
      }

      &::placeholder {
        opacity: 1;
        color: #ffffff;
      }
    }
    .form_field_prefix {
      width: 64px;
      height: 64px;
      position: absolute;
      overflow: hidden;
      background-color: #ffffff;
      border-radius: 50%;

      @media screen and (max-width: 1540px) {
        width: 56px;
        height: 56px;
        top: 12px;
        left: 7px;
      }
      @media screen and (max-width: 1100px) {
        width: 40px;
        height: 40px;
      }
      @media screen and (max-width: 767px) {
        top: 8px;
        left: 0px;
      }

      &_visible {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) scale(1);
        transition: all 0.3s;
      }

      &_hidden {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, 150%) scale(0.5);
        transition: all 0.3s;
      }

      svg {
        @media screen and (max-width: 1540px) {
          width: 24px;
          height: 24px;
        }
        @media screen and (max-width: 1100px) {
          width: 16px;
          height: 16px;
        }
      }
    }

    &_focused {
      .form_field_prefix {
        &_visible {
          transform: translate(-50%, -250%) scale(0.5);
        }
        &_hidden {
          transform: translate(-50%, -50%) scale(1);
        }
      }
    }
  }

  .search_result {
    position: absolute;
    top: 100%;
    z-index: 1000;
    padding: 12px 0;
    border-radius: 30px;
    margin: 0;
    list-style: none;
    box-shadow: 0 20px 70px 0 rgba(0, 0, 0, 0.05);
    max-height: 340px;
    overflow: auto;
    background-color: #ffffff;
    width: 100%;
    border: none;

    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background: #888;
      border-radius: 2px;
    }
    &::-webkit-scrollbar-thumb:hover {
      background: #555;
    }

    li {
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      padding: 16px 48px;
      font-family: "ITC Avant Garde Gothic Std";
      font-size: 16px;
      font-style: normal;
      font-weight: 300;
      line-height: 175%;
      color: #878c95;
      background-color: transparent;
      transition: all 0.3s;

      @media screen and (max-width: 1100px) {
        font-size: 12px;
      }

      @media screen and (max-width: 767px) {
        padding: 8px 26px;
      }

      &:hover {
        color: #202020;
        background-color: #e3f3ff;
      }
    }
  }
}
</style>
